
import React, { useState } from 'react'
import AddMatchForm from './AddMatchForm'
import ImportJSON from './ImportJSON'
import * as importAPI from '../services/import'
import * as playerAPI from '../services/player'
import * as matchAPI from '../services/matches'

export const outcomeText = (match) => {
  if (typeof match.outcome === 'undefined') {
    console.log('Missing outcome on match object')
    return 'Nieznany'
  }

  const outcomeStr = String(match.outcome)
  const p1 = match.player1 && match.player1.name ? match.player1.name : 'Ekipa 1'
  const p2 = match.player2 && match.player2.name ? match.player2.name : 'Ekipa 2'

  switch (outcomeStr) {
  case '1':
    return p1
  case '0':
    return p2
  case '0.5':
    return 'Remis'
  default:
    console.log('Unknown match outcome')
    return 'Nieznany'
  }
}

const MatchesDisplay = ({ matches, setMatches, players, setPlayers }) => {
  const [showImport, setShowImport] = useState(false)
  const [isImportingSample, setIsImportingSample] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [playerFilter, setPlayerFilter] = useState('')

  const handleImportComplete = () => {
    // Refresh data after import
    window.location.reload()
  }

  const handleSampleImport = async () => {
    setIsImportingSample(true)
    try {
      const result = await importAPI.importFromSampleFile()
      if (result.success) {
        // Sort matches by date before processing
        result.matches.sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
        result.matches.forEach(match => {
          playerAPI.updatePlayerRankings(match)
        })
        alert(`Przykładowe dane zaimportowane pomyślnie!\nZaimportowane mecze: ${result.matchesImported}\nNowe drużyny: ${result.playersCreated}`)
        window.location.reload()
      } else {
        alert(`Import nie powiódł się: ${result.error}`)
      }
    } catch (error) {
      alert(`Import nie powiódł się: ${error.message}`)
    } finally {
      setIsImportingSample(false)
    }
  }

  const handleClearAll = () => {
    if (window.confirm('Czy na pewno chcesz wyczyścić wszystkie dane? Tej operacji nie można cofnąć.')) {
      setIsClearing(true)
      try {
        // Call clearAll on playerAPI/matchAPI, but be defensive in case one of the services
        // was accidentally exported with a typo in the repository (e.g. "celarALL").
        if (typeof playerAPI.clearAll === 'function') {
          playerAPI.clearAll()
        } else if (typeof playerAPI.celarALL === 'function') {
          // fallback for a common typo in older/out-of-sync code
          playerAPI.celarALL()
        } else {
          console.warn('playerAPI.clearAll() not found; nothing to clear for players.')
        }

        if (typeof matchAPI.clearAll === 'function') {
          matchAPI.clearAll()
        } else if (typeof matchAPI.celarALL === 'function') {
          // fallback for a common typo in older/out-of-sync code
          matchAPI.celarALL()
        } else {
          console.warn('matchAPI.clearAll() not found; nothing to clear for matches.')
        }

        // Ensure localStorage keys are removed as a final fallback in case the services
        // didn't clear them correctly (common cause of "only table cleared" bug).
        try {
          localStorage.removeItem('matches')
          localStorage.removeItem('players')
        } catch (e) {
          console.warn('Failed to remove items from localStorage', e)
        }

        alert('Wszystkie dane zostały pomyślnie wyczyszczone!')
        window.location.reload()
      } catch (error) {
        alert(`Nie udało się wyczyścić danych: ${error.message}`)
      } finally {
        setIsClearing(false)
      }
    }
  }

  // Format dates using Polish locale: DD.MM.YYYY (date only, no time)
  const formatDate = (timestamp) => {
    const d = new Date(timestamp)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Filter matches by player name (searches in both player1 and player2)
  const filteredMatches = matches.filter(match => {
    if (!playerFilter.trim()) return true
    
    const filterLower = playerFilter.toLowerCase()
    return match.player1.name.toLowerCase().includes(filterLower) ||
           match.player2.name.toLowerCase().includes(filterLower)
  })

  // Compute per-match ELO deltas for display. This replays matches in chronological order
  // starting from a common baseline so the per-match change can be shown next to team names.
  const matchDeltas = playerAPI.computeMatchDeltas(matches || [], players || [])

  const formatDelta = (d) => {
    if (typeof d === 'undefined' || d === null) return ''
    const v = Math.round(Number(d) * 100) / 100
    return (v > 0 ? `(+${v})` : `(${v})`)
  }

  return (
    <div>
      <div className="matches-header">
        <h2>Mecze drużyn</h2>
        <div className="header-buttons">
          <button 
            onClick={handleSampleImport}
            disabled={isImportingSample}
            className="sample-import-btn"
          >
            {isImportingSample ? 'Importowanie...' : '📥 Załaduj przykładowe dane'}
          </button>
          <button 
            onClick={handleClearAll}
            disabled={isClearing}
            className="clear-btn"
          >
            {isClearing ? 'Czyszczenie...' : '🗑️ Wyczyść wszystkie dane'}
          </button>
          <button 
            onClick={() => setShowImport(!showImport)}
            className="import-toggle-btn"
          >
            {showImport ? 'Ukryj import' : 'Import z JSON'}
          </button>
        </div>
      </div>

      {showImport && (
        <ImportJSON onImportComplete={handleImportComplete} />
      )}

      <AddMatchForm players={players} matches={matches} setPlayers={setPlayers} setMatches={setMatches}/>
      
      <div className="filter-section">
        <label htmlFor="player-filter" className="filter-label">
          🔍 Filtruj po drużynie:
        </label>
        <input
          id="player-filter"
          type="text"
          value={playerFilter}
          onChange={(e) => setPlayerFilter(e.target.value)}
          placeholder="Wpisz nazwę drużyny..."
          className="filter-input"
        />
        {playerFilter && (
          <button 
            onClick={() => setPlayerFilter('')}
            className="clear-filter-btn"
            title="Wyczyść filtr"
          >
            ✕
          </button>
        )}
      </div>
      
      <table className="stripedTable matches-table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Ekipa 1</th>
            <th>Ekipa 2</th>
            <th>Zwycięzca</th>
            <th>Komentarz</th>
          </tr>
        </thead>
        <tbody>
          {filteredMatches.sort((l, r) => -(l.datetime - r.datetime)).map(match => {
            return (<tr key={match.id}>
              <td>{formatDate(match.datetime)}</td>
              <td>{match.player1.name} {matchDeltas && matchDeltas[match.id] ? formatDelta(matchDeltas[match.id].delta1) : ''}</td>
              <td>{match.player2.name} {matchDeltas && matchDeltas[match.id] ? formatDelta(matchDeltas[match.id].delta2) : ''}</td>
              <td>{outcomeText(match)}</td>
              <td className="comment-cell">
                {match.comment ? (
                  <span className="comment-text" title={match.comment}>
                    💬 {match.comment}
                  </span>
                ) : (
                  <span className="no-comment">—</span>
                )}
              </td>
            </tr>)
          })}
        </tbody>
      </table>
    </div>
  )
}

export default MatchesDisplay
